import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import { getWabaWebhookUrl } from '../../lib/publicApiBase';
import Button from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Textarea from '../../components/ui/Textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import Modal from '../../components/ui/Modal';
import { Info } from 'lucide-react';
import { Job, Invitation } from '../../types';
type WabaConnection = {
  id: string;
  organizationId: string;
  organization?: { id: string; name: string; type: string };
  wabaId: string;
  phoneNumberId: string;
  businessAccountId?: string | null;
  status: string;
  tokenExpiresAt?: string | null;
  lastValidatedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

type OutboxRow = {
  id: string;
  channel: string;
  status: string;
  messagingEventKey?: string | null;
  templateKey?: string | null;
  destination: string;
  payload: unknown;
  attempts: number;
  lastError?: string | null;
  providerMessageId?: string | null;
  scheduledAt: string;
  sentAt?: string | null;
  organizationId?: string | null;
  organization?: { id: string; name: string } | null;
  recipientUser?: { id: string; fullName?: string | null } | null;
  createdAt: string;
  updatedAt: string;
};

type ActionLinkRow = {
  id: string;
  invitationId: string;
  actionType: string;
  email: string;
  targetPath: string;
  expiresAt: string;
  maxUses: number;
  useCount: number;
  createdAt: string;
  invitation?: { id: string; job?: { id: string; title: string } | null } | null;
};

const ACTION_TYPES = [
  'CONTRACT_SIGN',
  'SCRIPT_APPROVE',
  'SCRIPT_SUBMIT',
  'SUBMISSION_UPLOAD',
  'SUBMISSION_REVISE',
  'INVITATION_RESPOND',
] as const;

const OUTBOX_STATUSES = ['', 'PENDING', 'SENT', 'FAILED'] as const;
const OUTBOX_CHANNELS = ['', 'WHATSAPP', 'EMAIL', 'IN_APP', 'SMS'] as const;

function formatTs(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

type TabHelpId = 'whatsapp' | 'outbox' | 'action-links';

const TAB_HELP: Record<
  TabHelpId,
  { title: string; summary: string; steps: string[] }
> = {
  whatsapp: {
    title: 'WhatsApp (WABA)',
    summary:
      'Connect your Meta WhatsApp Business API to this agency so the platform can send WhatsApp messages from the notification outbox.',
    steps: [
      'In Meta Developer Console, create or open your WhatsApp app and note WABA ID, Phone number ID, and a long-lived access token.',
      'Paste those into “Create or update connection” for the correct agency organization. The access token is encrypted on the server.',
      'Set “Webhook verify token” to match Meta’s “Verify token”, then paste the callback URL shown here into Meta’s webhook settings.',
      'After saving, use “Send test message” to enqueue a row and process it (see Notification outbox tab).',
    ],
  },
  outbox: {
    title: 'Notification outbox',
    summary:
      'The outbox is a queue of outbound messages. Each row has a channel (IN_APP, WHATSAPP, EMAIL, SMS), a destination, a status, and optional error text.',
    steps: [
      'When the app notifies someone, it can create IN_APP rows (bell notifications) and—if enabled—WHATSAPP, EMAIL, or SMS rows for the same event.',
      'Statuses: PENDING → a worker sends the message; SENT means done; FAILED shows lastError (e.g. SMTP not configured, or invalid WhatsApp token).',
      'Email uses SMTP env vars on the server. SMS rows may fail until a provider (e.g. Twilio) is integrated.',
      'Admins can run “process batch” to drain pending rows manually (same as test send).',
    ],
  },
  'action-links': {
    title: 'Action links',
    summary:
      'An action link is a URL you share with a KOL that opens a secure page, redeems a token, and signs them in for that browser session—then sends them to the right task (contract, script, etc.).',
    steps: [
      'Pick the campaign (job) and the invited KOL. The email must match the KOL on that invitation (required by the API).',
      'Choose the action type and expiry. Click Create link, then copy the URL to send via WhatsApp, email, or another channel.',
      'When the KOL opens the link, the app sets session cookies (same style as password login) and redirects to the task. It is not a “link-only” reminder: redeem logs them in without typing a password for that visit.',
      'Links can be limited by max uses and expiry; a used or expired link returns an error.',
    ],
  },
};

interface MessagingIntegrationsPageProps {
  isAdminView: boolean;
}

export default function MessagingIntegrationsPage({ isAdminView }: MessagingIntegrationsPageProps) {
  const { user } = useAuthStore();
  const [agencyOrgs, setAgencyOrgs] = useState<{ id: string; name: string }[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');

  const [helpTab, setHelpTab] = useState<TabHelpId | null>(null);

  const [connections, setConnections] = useState<WabaConnection[]>([]);
  const [wabaLoading, setWabaLoading] = useState(false);
  const [wabaError, setWabaError] = useState('');

  const [upsertWabaId, setUpsertWabaId] = useState('');
  const [upsertPhoneId, setUpsertPhoneId] = useState('');
  const [upsertBusinessId, setUpsertBusinessId] = useState('');
  const [upsertAccessToken, setUpsertAccessToken] = useState('');
  const [upsertVerifyToken, setUpsertVerifyToken] = useState('');
  const [upsertExpiresAt, setUpsertExpiresAt] = useState('');
  const [upsertStatus, setUpsertStatus] = useState<'ACTIVE' | 'INACTIVE' | 'ERROR'>('ACTIVE');

  const [testTo, setTestTo] = useState('');
  const [testText, setTestText] = useState('myKOL test notification');
  const [testTemplateKey, setTestTemplateKey] = useState('');

  const [outboxRows, setOutboxRows] = useState<OutboxRow[]>([]);
  const [outboxLoading, setOutboxLoading] = useState(false);
  const [outboxError, setOutboxError] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterChannel, setFilterChannel] = useState<string>('');
  const [filterOrgId, setFilterOrgId] = useState<string>('');

  const [processLimit, setProcessLimit] = useState(25);
  const [processResult, setProcessResult] = useState<unknown>(null);

  const [actionLinks, setActionLinks] = useState<ActionLinkRow[]>([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const [linksError, setLinksError] = useState('');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [invLoading, setInvLoading] = useState(false);
  const [selectedInvitationId, setSelectedInvitationId] = useState('');
  const [createActionType, setCreateActionType] = useState<string>('CONTRACT_SIGN');
  const [createEmail, setCreateEmail] = useState('');
  const [createExpiresMin, setCreateExpiresMin] = useState(1440);
  const [createMaxUses, setCreateMaxUses] = useState(1);
  const [lastRedirect, setLastRedirect] = useState('');

  const webhookUrl = useMemo(() => getWabaWebhookUrl(), []);

  const loadAgencyOrgs = useCallback(async () => {
    if (isAdminView) {
      const res = await api.get<{ items: { id: string; name: string; type: string }[] }>(
        '/admin/organizations',
        { params: { type: 'AGENCY', limit: 200 } }
      );
      const items = res.data.items || [];
      setAgencyOrgs(items.map((o) => ({ id: o.id, name: o.name })));
      setSelectedOrgId((prev) => prev || items[0]?.id || '');
      setFilterOrgId('');
      return;
    }
    const memberships = user?.memberships?.filter((m) => m.organization?.type === 'AGENCY') || [];
    const orgs = memberships.map((m) => ({
      id: m.organizationId,
      name: m.organization?.name || m.organizationId,
    }));
    setAgencyOrgs(orgs);
    if (orgs.length === 1) {
      const only = orgs[0].id;
      setSelectedOrgId(only);
      setFilterOrgId(only);
    } else {
      setSelectedOrgId((prev) => prev || orgs[0]?.id || '');
    }
  }, [isAdminView, user?.memberships]);

  const loadConnections = useCallback(async () => {
    setWabaLoading(true);
    setWabaError('');
    try {
      const res = await api.get<WabaConnection[]>('/waba/connections');
      setConnections(res.data);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to load WABA connections';
      setWabaError(msg);
    } finally {
      setWabaLoading(false);
    }
  }, []);

  const loadOutbox = useCallback(async () => {
    setOutboxLoading(true);
    setOutboxError('');
    try {
      const params: Record<string, string | number> = { limit: 100 };
      if (filterStatus) params.status = filterStatus;
      if (filterChannel) params.channel = filterChannel;
      if (filterOrgId) params.organizationId = filterOrgId;
      const res = await api.get<OutboxRow[]>('/notification-outbox', { params });
      setOutboxRows(res.data);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to load notification outbox';
      setOutboxError(msg);
    } finally {
      setOutboxLoading(false);
    }
  }, [filterStatus, filterChannel, filterOrgId]);

  const loadActionLinks = useCallback(async () => {
    setLinksLoading(true);
    setLinksError('');
    try {
      const res = await api.get<ActionLinkRow[]>('/action-links/my-links');
      setActionLinks(res.data);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to load action links';
      setLinksError(msg);
    } finally {
      setLinksLoading(false);
    }
  }, []);

  const loadJobsForOrg = useCallback(async (orgId: string) => {
    if (!orgId) {
      setJobs([]);
      return;
    }
    setJobsLoading(true);
    try {
      const res = await api.get<Job[]>('/jobs', { params: { managedByOrgId: orgId } });
      setJobs(res.data);
    } catch {
      setJobs([]);
    } finally {
      setJobsLoading(false);
    }
  }, []);

  const loadInvitationsForJob = useCallback(async (jobId: string) => {
    if (!jobId) {
      setInvitations([]);
      return;
    }
    setInvLoading(true);
    try {
      const res = await api.get<Invitation[]>('/invitations', { params: { jobId } });
      setInvitations(res.data);
    } catch {
      setInvitations([]);
    } finally {
      setInvLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAgencyOrgs();
  }, [loadAgencyOrgs]);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  useEffect(() => {
    loadOutbox();
  }, [loadOutbox]);

  useEffect(() => {
    loadActionLinks();
  }, [loadActionLinks]);

  useEffect(() => {
    if (selectedOrgId) {
      void loadJobsForOrg(selectedOrgId);
    } else {
      setJobs([]);
    }
  }, [selectedOrgId, loadJobsForOrg]);

  useEffect(() => {
    setSelectedJobId('');
    setSelectedInvitationId('');
    setCreateEmail('');
  }, [selectedOrgId]);

  useEffect(() => {
    if (selectedJobId) void loadInvitationsForJob(selectedJobId);
    else {
      setInvitations([]);
      setSelectedInvitationId('');
      setCreateEmail('');
    }
  }, [selectedJobId, loadInvitationsForJob]);

  useEffect(() => {
    const inv = invitations.find((i) => i.id === selectedInvitationId);
    const em = inv?.kolProfile?.email?.trim() || '';
    setCreateEmail(em);
  }, [selectedInvitationId, invitations]);

  const connectionForSelected = connections.find((c) => c.organizationId === selectedOrgId);

  const onSaveConnection = async () => {
    if (!selectedOrgId) {
      setWabaError('Select an agency organization first.');
      return;
    }
    setWabaError('');
    try {
      await api.post('/waba/connections', {
        organizationId: selectedOrgId,
        wabaId: upsertWabaId.trim(),
        phoneNumberId: upsertPhoneId.trim(),
        businessAccountId: upsertBusinessId.trim() || undefined,
        accessToken: upsertAccessToken.trim(),
        webhookVerifyToken: upsertVerifyToken.trim() || undefined,
        tokenExpiresAt: upsertExpiresAt ? new Date(upsertExpiresAt).toISOString() : undefined,
        status: upsertStatus,
      });
      setUpsertAccessToken('');
      await loadConnections();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to save connection';
      setWabaError(msg);
    }
  };

  const onSendTest = async () => {
    if (!selectedOrgId) {
      setWabaError('Select an agency organization first.');
      return;
    }
    setWabaError('');
    try {
      await api.post(`/waba/connections/${encodeURIComponent(selectedOrgId)}/send-test`, {
        to: testTo.trim(),
        text: testText.trim(),
        templateKey: testTemplateKey.trim(),
      });
      setTestTo('');
      await loadOutbox();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Send test failed';
      setWabaError(msg);
    }
  };

  const onProcessOutbox = async () => {
    setProcessResult(null);
    try {
      const res = await api.post('/notification-outbox/process', { limit: processLimit });
      setProcessResult(res.data);
      await loadOutbox();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Process batch failed';
      setProcessResult({ error: msg });
    }
  };

  const onCreateActionLink = async () => {
    setLinksError('');
    setLastRedirect('');
    if (!selectedInvitationId) {
      setLinksError('Select a campaign and KOL invitation.');
      return;
    }
    if (!createEmail.trim()) {
      setLinksError('This invitation has no KOL email on file; add one to the KOL profile first.');
      return;
    }
    try {
      const res = await api.post<{
        redirect: string;
        id: string;
      }>('/action-links', {
        invitationId: selectedInvitationId,
        actionType: createActionType,
        email: createEmail.trim().toLowerCase(),
        expiresInMinutes: createExpiresMin,
        maxUses: createMaxUses,
      });
      setLastRedirect(res.data.redirect);
      setSelectedInvitationId('');
      setSelectedJobId('');
      await loadActionLinks();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to create action link';
      setLinksError(msg);
    }
  };

  const help = helpTab ? TAB_HELP[helpTab] : null;

  function TabWithInfo({
    value,
    label,
    helpId,
  }: {
    value: string;
    label: string;
    helpId: TabHelpId;
  }) {
    return (
      <div className="inline-flex items-center gap-0.5">
        <TabsTrigger value={value}>{label}</TabsTrigger>
        <button
          type="button"
          className="rounded-full p-1 text-glass-text-2 hover:text-accent2 hover:bg-glass-3"
          aria-label={`About ${label}`}
          onClick={() => setHelpTab(helpId)}
        >
          <Info className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl">
      {help && (
        <Modal isOpen={!!helpTab} onClose={() => setHelpTab(null)} title={help.title} size="lg">
          <p className="text-sm text-glass-text-2 mb-3">{help.summary}</p>
          <ol className="list-decimal list-inside space-y-2 text-sm text-glass-text">
            {help.steps.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ol>
        </Modal>
      )}

      <div>
        <h1 className="text-[2.25rem] font-semibold tracking-tightest text-glass-text">
          WhatsApp &amp; messaging
        </h1>
        <p className="text-glass-text-2 text-sm mt-1">
          Connect WhatsApp, review the notification queue, and create KOL action links. Edit copy on{' '}
          <a href="/agency/messaging-templates" className="text-accent2 hover:underline">
            Messaging templates
          </a>
          . See <code className="text-accent2">test_waba.md</code> for API checks.
        </p>
      </div>

      {(agencyOrgs.length > 1 || isAdminView) && (
        <Card>
          <CardHeader>
            <CardTitle>Agency context</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <label className="text-xs uppercase tracking-wider text-glass-text-2">Organization</label>
            <Select
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              className="max-w-xl"
            >
              <option value="">Select agency…</option>
              {agencyOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </Select>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="whatsapp">
        <TabsList className="flex flex-wrap h-auto gap-1 py-1">
          <TabWithInfo value="whatsapp" label="WhatsApp (WABA)" helpId="whatsapp" />
          <TabWithInfo value="outbox" label="Notification outbox" helpId="outbox" />
          <TabWithInfo value="action-links" label="Action links" helpId="action-links" />
        </TabsList>

        <TabsContent value="whatsapp" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Meta webhook URL</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p className="text-glass-text-2">
                Set this in Meta as the callback URL. The verify token must match{' '}
                <strong className="text-glass-text">webhookVerifyToken</strong> saved on a connection.
              </p>
              <div className="flex flex-wrap gap-2 items-center">
                <code className="flex-1 min-w-[12rem] break-all bg-glass-2 border border-glass rounded-lg px-3 py-2 text-xs">
                  {webhookUrl}
                </code>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => navigator.clipboard.writeText(webhookUrl)}
                >
                  Copy
                </Button>
              </div>
              {!import.meta.env.VITE_API_URL && !import.meta.env.VITE_PUBLIC_API_URL && (
                <p className="text-amber-600/90 dark:text-amber-400/90 text-xs">
                  For local dev behind the Vite proxy, set <code>VITE_PUBLIC_API_URL</code> to your API
                  origin (e.g. http://localhost:3000) so this URL points at the backend, not the dev server.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
              <CardTitle>Connections</CardTitle>
              <Button size="sm" variant="outline" onClick={loadConnections} disabled={wabaLoading}>
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              {wabaError && (
                <p className="text-sm text-red-600 dark:text-red-400 mb-3">{wabaError}</p>
              )}
              {wabaLoading ? (
                <p className="text-glass-text-2 text-sm">Loading…</p>
              ) : connections.length === 0 ? (
                <p className="text-glass-text-2 text-sm">No connections yet.</p>
              ) : (
                <div className="overflow-x-auto border border-glass rounded-xl">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-glass bg-glass-2 text-left text-glass-text-2">
                        <th className="p-2 font-medium">Org</th>
                        <th className="p-2 font-medium">WABA ID</th>
                        <th className="p-2 font-medium">Phone # ID</th>
                        <th className="p-2 font-medium">Status</th>
                        <th className="p-2 font-medium">Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {connections.map((c) => (
                        <tr key={c.id} className="border-b border-glass/60">
                          <td className="p-2">{c.organization?.name || c.organizationId}</td>
                          <td className="p-2 font-mono text-xs">{c.wabaId}</td>
                          <td className="p-2 font-mono text-xs">{c.phoneNumberId}</td>
                          <td className="p-2">{c.status}</td>
                          <td className="p-2 text-glass-text-2">{formatTs(c.updatedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Create or update connection</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-glass-text-2">
                One connection per agency org (upsert). Access token is stored encrypted on the server
                and never returned in list responses.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-xs text-glass-text-2">WABA ID</label>
                  <Input value={upsertWabaId} onChange={(e) => setUpsertWabaId(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-glass-text-2">Phone number ID</label>
                  <Input value={upsertPhoneId} onChange={(e) => setUpsertPhoneId(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-glass-text-2">Business account ID (optional)</label>
                  <Input value={upsertBusinessId} onChange={(e) => setUpsertBusinessId(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-glass-text-2">Access token</label>
                  <Input
                    type="password"
                    autoComplete="off"
                    value={upsertAccessToken}
                    onChange={(e) => setUpsertAccessToken(e.target.value)}
                    placeholder="From Meta"
                  />
                </div>
                <div>
                  <label className="text-xs text-glass-text-2">Webhook verify token (optional)</label>
                  <Input
                    value={upsertVerifyToken}
                    onChange={(e) => setUpsertVerifyToken(e.target.value)}
                    placeholder='Must match Meta "Verify token"'
                  />
                </div>
                <div>
                  <label className="text-xs text-glass-text-2">Token expires at (optional)</label>
                  <Input
                    type="datetime-local"
                    value={upsertExpiresAt}
                    onChange={(e) => setUpsertExpiresAt(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs text-glass-text-2">Status</label>
                  <Select
                    value={upsertStatus}
                    onChange={(e) =>
                      setUpsertStatus(e.target.value as 'ACTIVE' | 'INACTIVE' | 'ERROR')
                    }
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                    <option value="ERROR">ERROR</option>
                  </Select>
                </div>
              </div>
              <Button type="button" onClick={onSaveConnection}>
                Save connection
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Send test message</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {connectionForSelected && connectionForSelected.status !== 'ACTIVE' && (
                <p className="text-amber-600/90 dark:text-amber-400/90 text-sm">
                  Active connection required for this org. Current status: {connectionForSelected.status}
                </p>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="text-xs text-glass-text-2">To (E.164)</label>
                  <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="+15551234567" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-glass-text-2">Message text</label>
                  <Textarea value={testText} onChange={(e) => setTestText(e.target.value)} rows={2} />
                </div>
                <div>
                  <label className="text-xs text-glass-text-2">Template key (optional)</label>
                  <Input value={testTemplateKey} onChange={(e) => setTestTemplateKey(e.target.value)} />
                </div>
              </div>
              <Button type="button" variant="outline" onClick={onSendTest}>
                Send test (enqueue + process)
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="outbox" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
              <CardTitle>Outbox entries</CardTitle>
              <Button size="sm" variant="outline" onClick={loadOutbox} disabled={outboxLoading}>
                Refresh
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <label className="text-xs text-glass-text-2 block mb-1">Status</label>
                  <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    {OUTBOX_STATUSES.map((s) => (
                      <option key={s || 'all'} value={s}>
                        {s || 'All'}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-glass-text-2 block mb-1">Channel</label>
                  <Select value={filterChannel} onChange={(e) => setFilterChannel(e.target.value)}>
                    {OUTBOX_CHANNELS.map((c) => (
                      <option key={c || 'all'} value={c}>
                        {c || 'All'}
                      </option>
                    ))}
                  </Select>
                </div>
                {isAdminView && (
                  <div>
                    <label className="text-xs text-glass-text-2 block mb-1">Organization filter</label>
                    <Select value={filterOrgId} onChange={(e) => setFilterOrgId(e.target.value)}>
                      <option value="">All (visible)</option>
                      {agencyOrgs.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                )}
              </div>

              {outboxError && (
                <p className="text-sm text-red-600 dark:text-red-400">{outboxError}</p>
              )}

              {isAdminView && (
                <div className="flex flex-wrap gap-2 items-center p-3 rounded-xl border border-glass bg-glass-2/50">
                  <span className="text-sm text-glass-text-2">Admin: process pending batch</span>
                  <Input
                    type="number"
                    className="w-24"
                    min={1}
                    max={100}
                    value={processLimit}
                    onChange={(e) => setProcessLimit(Number(e.target.value) || 25)}
                  />
                  <Button type="button" size="sm" onClick={onProcessOutbox}>
                    Run processOutboxBatch
                  </Button>
                  {processResult != null && (
                    <pre className="text-xs bg-glass-1 border border-glass rounded-lg p-2 max-w-full overflow-auto">
                      {JSON.stringify(processResult, null, 2)}
                    </pre>
                  )}
                </div>
              )}

              {outboxLoading ? (
                <p className="text-glass-text-2 text-sm">Loading…</p>
              ) : outboxRows.length === 0 ? (
                <p className="text-glass-text-2 text-sm">No rows match filters.</p>
              ) : (
                <div className="overflow-x-auto border border-glass rounded-xl">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-glass bg-glass-2 text-left text-glass-text-2">
                        <th className="p-2 font-medium">Status</th>
                        <th className="p-2 font-medium">Channel</th>
                        <th className="p-2 font-medium">Event</th>
                        <th className="p-2 font-medium">Destination</th>
                        <th className="p-2 font-medium">Org</th>
                        <th className="p-2 font-medium">Attempts</th>
                        <th className="p-2 font-medium">Error</th>
                        <th className="p-2 font-medium">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {outboxRows.map((r) => (
                        <tr key={r.id} className="border-b border-glass/60 align-top">
                          <td className="p-2">{r.status}</td>
                          <td className="p-2">{r.channel}</td>
                          <td className="p-2 text-xs">{r.messagingEventKey || '—'}</td>
                          <td className="p-2 font-mono text-xs break-all max-w-[10rem]">{r.destination}</td>
                          <td className="p-2">{r.organization?.name || r.organizationId || '—'}</td>
                          <td className="p-2">{r.attempts}</td>
                          <td className="p-2 text-xs text-red-500/90 max-w-[14rem] break-words">
                            {r.lastError || '—'}
                          </td>
                          <td className="p-2 text-glass-text-2 whitespace-nowrap">
                            {formatTs(r.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="action-links" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Create action link</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-glass-text-2">
                Pick the campaign and KOL. Opening the link redeems the token, sets the KOL session
                cookies for this browser, and redirects to the task (no password typed for that visit).
              </p>
              {linksError && (
                <p className="text-sm text-red-600 dark:text-red-400">{linksError}</p>
              )}
              {!selectedOrgId && (
                <p className="text-sm text-amber-600/90">Select an agency organization first.</p>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="text-xs text-glass-text-2">Campaign (job)</label>
                  <Select
                    value={selectedJobId}
                    onChange={(e) => setSelectedJobId(e.target.value)}
                    disabled={!selectedOrgId || jobsLoading}
                  >
                    <option value="">{jobsLoading ? 'Loading jobs…' : 'Select job…'}</option>
                    {jobs.map((j) => (
                      <option key={j.id} value={j.id}>
                        {j.title}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-glass-text-2">KOL (invitation)</label>
                  <Select
                    value={selectedInvitationId}
                    onChange={(e) => setSelectedInvitationId(e.target.value)}
                    disabled={!selectedJobId || invLoading}
                  >
                    <option value="">
                      {!selectedJobId
                        ? 'Select a job first'
                        : invLoading
                          ? 'Loading…'
                          : invitations.length === 0
                            ? 'No invitations for this job'
                            : 'Select KOL…'}
                    </option>
                    {invitations.map((inv) => (
                      <option key={inv.id} value={inv.id}>
                        {inv.kolProfile?.name || 'KOL'} · {inv.kolProfile?.email || 'no email'}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-glass-text-2">Action type</label>
                  <Select
                    value={createActionType}
                    onChange={(e) => setCreateActionType(e.target.value)}
                  >
                    {ACTION_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-glass-text-2">KOL email (from invitation)</label>
                  <Input type="email" value={createEmail} readOnly className="bg-glass-2/50" />
                </div>
                <div>
                  <label className="text-xs text-glass-text-2">Expires in (minutes)</label>
                  <Input
                    type="number"
                    min={5}
                    value={createExpiresMin}
                    onChange={(e) => setCreateExpiresMin(Number(e.target.value) || 1440)}
                  />
                </div>
                <div>
                  <label className="text-xs text-glass-text-2">Max uses</label>
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    value={createMaxUses}
                    onChange={(e) => setCreateMaxUses(Number(e.target.value) || 1)}
                  />
                </div>
              </div>
              <Button type="button" onClick={onCreateActionLink} disabled={!selectedOrgId}>
                Create link
              </Button>
              {lastRedirect && (
                <div className="mt-2 space-y-1">
                  <p className="text-sm text-glass-text-2">Action link URL (share with KOL):</p>
                  <div className="flex flex-wrap gap-2 items-center">
                    <code className="flex-1 min-w-[12rem] break-all bg-glass-2 border border-glass rounded-lg px-3 py-2 text-xs">
                      {lastRedirect}
                    </code>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => navigator.clipboard.writeText(lastRedirect)}
                    >
                      Copy
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
              <CardTitle>Your recent links</CardTitle>
              <Button size="sm" variant="outline" onClick={loadActionLinks} disabled={linksLoading}>
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              {linksLoading ? (
                <p className="text-glass-text-2 text-sm">Loading…</p>
              ) : actionLinks.length === 0 ? (
                <p className="text-glass-text-2 text-sm">No links yet.</p>
              ) : (
                <div className="overflow-x-auto border border-glass rounded-xl">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-glass bg-glass-2 text-left text-glass-text-2">
                        <th className="p-2 font-medium">Type</th>
                        <th className="p-2 font-medium">Email</th>
                        <th className="p-2 font-medium">Job</th>
                        <th className="p-2 font-medium">Uses</th>
                        <th className="p-2 font-medium">Expires</th>
                      </tr>
                    </thead>
                    <tbody>
                      {actionLinks.map((l) => (
                        <tr key={l.id} className="border-b border-glass/60">
                          <td className="p-2">{l.actionType}</td>
                          <td className="p-2">{l.email}</td>
                          <td className="p-2">{l.invitation?.job?.title || '—'}</td>
                          <td className="p-2">
                            {l.useCount}/{l.maxUses}
                          </td>
                          <td className="p-2 text-glass-text-2">{formatTs(l.expiresAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
}
